import Decimal from 'decimal.js';
import { Fragment } from 'react';

import {
  BillableTransactionCategory,
  type BillDTO,
  type BillLineDTO,
} from '@invenco/accounts-interface';

import { Badge } from '@/components/core/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Skeleton } from '@/components/core/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/core/table';
import { DataTableCardEmpty } from '@/components/data-table';
import { formatMoney, formatNumber } from '@/shared/helpers';

const categoryOrder = [
  BillableTransactionCategory.FULFILLMENT,
  BillableTransactionCategory.INVENTORY,
  BillableTransactionCategory.SHIPPING,
  BillableTransactionCategory.PLATFORM,
];

const categoryTitle: Record<BillableTransactionCategory, string> = {
  [BillableTransactionCategory.FULFILLMENT]: 'Fulfillment',
  [BillableTransactionCategory.INVENTORY]: 'Inventory',
  [BillableTransactionCategory.SHIPPING]: 'Shipping',
  [BillableTransactionCategory.PLATFORM]: 'Platform',
};

function formatQuantity(quantity?: number) {
  if (quantity === undefined) {
    return undefined;
  }

  return formatNumber(quantity, Number.isInteger(quantity) ? 0 : 3);
}

function getLinesByCategory(billLines: BillLineDTO[]) {
  return categoryOrder
    .map((category) => ({
      category,
      lines: billLines.filter((line) => line.category === category),
    }))
    .filter((group) => group.lines.length > 0);
}

function ChargesLoadingRows() {
  return (
    <>
      {[0, 1, 2].map((row) => (
        <TableRow key={row}>
          <TableCell>
            <Skeleton className="h-4 w-44" />
          </TableCell>
          <TableCell>
            <Skeleton className="ml-auto h-4 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="ml-auto h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="ml-auto h-4 w-20" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function ChargesTable({ bill, isLoading }: { bill?: BillDTO; isLoading: boolean }) {
  const billLines = bill?.billLines ?? [];
  const groups = getLinesByCategory(billLines);
  const isEmpty = !isLoading && groups.length === 0;

  return (
    <>
      {isEmpty ? <DataTableCardEmpty>No charges found</DataTableCardEmpty> : null}
      <Table>
        {!isEmpty ? (
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Charge qty</TableHead>
              <TableHead className="text-right">Charge rate</TableHead>
              <TableHead className="text-right">Charge total</TableHead>
            </TableRow>
          </TableHeader>
        ) : null}
        <TableBody>
          {isLoading ? <ChargesLoadingRows /> : null}
          {!isLoading
            ? groups.map(({ category, lines }) => {
                const subtotal = lines.reduce(
                  (sum, line) => sum.plus(line.chargeTotal ?? 0),
                  new Decimal(0),
                );

                return (
                  <Fragment key={category}>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableCell colSpan={4} className="font-medium">
                        {categoryTitle[category]}
                      </TableCell>
                    </TableRow>
                    {lines.map((line) => (
                      <TableRow key={line.id} data-testid={`bill-line-${category}`}>
                        <TableCell>
                          {[line.subCategory, line.chargeType].filter(Boolean).join(' > ')}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatQuantity(line.chargeQty)}
                        </TableCell>
                        <TableCell className="text-right">
                          {`$${formatNumber(line.chargeRate ?? 0, 4)}`}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatMoney(line.chargeTotal ?? 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-medium">
                        Subtotal
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatMoney(subtotal.toNumber())}
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            : null}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={3} className="text-right font-medium">
              Total
            </TableCell>
            <TableCell className="text-right font-medium">
              {isLoading ? (
                <Skeleton className="ml-auto h-4 w-24" />
              ) : (
                formatMoney(bill?.total ?? 0)
              )}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </>
  );
}

export function ChargesCard({ bill, isLoading }: { bill?: BillDTO; isLoading: boolean }) {
  return (
    <Card aria-label="Charges">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>Charges</span>
          <Badge variant="secondary" aria-label={`with ${bill?.billLines?.length ?? 0} rows`}>
            {bill?.billLines?.length ?? 0}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ChargesTable bill={bill} isLoading={isLoading} />
      </CardContent>
    </Card>
  );
}
